import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import {
  createCheckout,
  downloadProcessedFile,
  getUploadStatus,
  uploadEepromFile,
} from '../../eeprom/api';

const toHex = (value) =>
  (Number(value) & 0xff).toString(16).toUpperCase().padStart(2, '0');

const formatHexView = (bytes, bytesPerRow = 5) => {
  const rows = [];
  const hexRowWidth = bytesPerRow * 3 - 1;
  for (let i = 0; i < bytes.length; i += bytesPerRow) {
    const slice = bytes.slice(i, i + bytesPerRow);
    const sliceArray = Array.from(slice);
    const hex = sliceArray.map((b) => toHex(b)).join(' ').padEnd(hexRowWidth, ' ');
    const ascii = sliceArray
      .map((b) => (b >= 32 && b <= 126 ? String.fromCharCode(b) : '.'))
      .join('');
    rows.push({
      offset: i.toString(16).toUpperCase().padStart(6, '0'),
      hex,
      hexBytes: sliceArray.map((b) => toHex(b)),
      ascii,
    });
  }
  return rows;
};

const F430Airbag = () => {
  const [fileName, setFileName] = useState('');
  const [bytes, setBytes] = useState(null);
  const [vinInput, setVinInput] = useState('');
  const normalizedVin = vinInput.trim().toUpperCase();
  const vinIsValid = /^ZFF[A-Z0-9]{14}$/.test(normalizedVin);
  const [upload, setUpload] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [isProcessed, setIsProcessed] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [processedLabel, setProcessedLabel] = useState('crash-cleared');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { user } = useAuth();

  const rows = useMemo(() => {
    if (!bytes) return [];
    return formatHexView(bytes);
  }, [bytes]);

  const newFileName = useMemo(() => {
    if (!upload?.originalFileName) return '';
    const name = upload.originalFileName;
    const dotIndex = name.lastIndexOf('.');
    const baseName = dotIndex >= 0 ? name.slice(0, dotIndex) : name;
    const ext = dotIndex >= 0 ? name.slice(dotIndex) : '.bin';
    const suffix = processedLabel || 'new';
    return `${baseName}_${suffix}${ext}`;
  }, [upload, processedLabel]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const uploadId =
      params.get('uploadId') ||
      window.localStorage.getItem('eeprom:lastUpload:f430-airbag');
    if (!uploadId || !user) return;
    getUploadStatus({ uploadId })
      .then((result) => {
        setUpload(result);
        setFileName(result.originalFileName || '');
        setIsProcessed(false);
        setProcessedLabel('crash-cleared');
        setHasDownloaded(false);
        setSuccessMessage(
          result.alreadyPaid
            ? 'Payment confirmed. You can process your uploaded file.'
            : 'Payment is still pending.'
        );
      })
      .catch(() => {
        setError('Unable to load upload status.');
      });
  }, [user]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setUpload(null);
    setIsProcessed(false);
    setProcessedLabel('crash-cleared');
    setHasDownloaded(false);
    setSuccessMessage('');
    setError('');

    const reader = new FileReader();
    reader.onload = () => {
      const buffer = reader.result;
      if (!(buffer instanceof ArrayBuffer)) return;
      setBytes(new Uint8Array(buffer));
    };
    reader.readAsArrayBuffer(file);
  };

  const handleUpload = async () => {
    if (!user) {
      setError('Please sign in to upload your EEPROM file.');
      return;
    }
    if (!bytes) {
      setError('Please select a file first.');
      return;
    }
    if (!vinIsValid) {
      setError('Please enter a valid VIN starting with ZFF (17 characters).');
      return;
    }
    setIsUploading(true);
    setError('');
    setSuccessMessage('');
    try {
      const fileInput = document.querySelector('#f430-airbag-file');
      const file = fileInput?.files?.[0];
      if (!file) {
        throw new Error('Please select a file first.');
      }
      const result = await uploadEepromFile({
        file,
        module: 'f430-airbag',
        selections: {},
        vin: normalizedVin,
      });
      setUpload(result);
      setIsProcessed(false);
      setProcessedLabel('crash-cleared');
      setHasDownloaded(false);
      window.localStorage.setItem('eeprom:lastUpload:f430-airbag', result.uploadId);
      if (result.alreadyPaid) {
        setSuccessMessage('Payment already on file for this VIN. You can process it now.');
      } else {
        setSuccessMessage('File verified. Continue to payment to download.');
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handlePay = async () => {
    if (!upload?.uploadId) return;
    setIsPaying(true);
    try {
      const { url } = await createCheckout({ uploadId: upload.uploadId });
      if (url) {
        window.location.href = url;
      }
    } catch (err) {
      setError(err.message || 'Payment failed');
    } finally {
      setIsPaying(false);
    }
  };

  const handleDownload = async () => {
    if (!upload?.uploadId) return;
    try {
      const blob = await downloadProcessedFile({ uploadId: upload.uploadId });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      if (newFileName) {
        anchor.download = newFileName;
      }
      anchor.click();
      window.URL.revokeObjectURL(url);
      setHasDownloaded(true);
      setSuccessMessage(
        'Congratulations! File Downloaded. You may Process the file again if you need to change the options'
      );
    } catch (err) {
      setError(err.message || 'Download failed');
    }
  };

  const handleProcess = () => {
    setProcessedLabel('crash-cleared');
    setIsProcessed(true);
    setHasDownloaded(false);
    setSuccessMessage('Processing complete. You can download your updated file.');
  };

  const canDownload = Boolean(upload?.alreadyPaid && isProcessed);
  const stepSelected = Boolean(fileName);
  const stepUploaded = Boolean(upload);
  const stepPaid = Boolean(upload?.alreadyPaid);
  const stepProcessed = Boolean(isProcessed);
  const stepDownloaded = Boolean(hasDownloaded);

  return (
    <div className="eeprom-detail">
      <h2>Ferrari F430 Airbag ECU</h2>
      <p>
        This module erases the crash record stored on the airbag ECU. Use the
        videos below to learn how to remove the ECU and extract the EEPROM file.
        Then click the button to pay and upload your file.
      </p>
      <div className="eeprom-video-placeholder">
        Airbag ECU removal video placeholder (YouTube link coming soon)
      </div>
      <div className="eeprom-video-placeholder">
        EEPROM extraction video placeholder (link coming soon)
      </div>
      {upload && Number.isFinite(upload.priceCents) && (
        <div className="price-banner">
          Price: ${(upload.priceCents / 100).toFixed(2)}
        </div>
      )}
      {upload?.originalFileName && (
        <div className="file-name-banner">
          Original file: <strong>{upload.originalFileName}</strong>
        </div>
      )}
      {upload?.originalFileName && isProcessed && (
        <div className="file-name-banner">
          New file: <strong>{newFileName}</strong>
        </div>
      )}
      <div className="vin-input">
        <label>VIN (required)</label>
        <input
          type="text"
          placeholder="Enter VIN (e.g., ZFFEZ58B000458992)"
          value={vinInput}
          onChange={(event) => setVinInput(event.target.value)}
        />
        {!vinIsValid && vinInput.length > 0 && (
          <small className="vin-hint">
            VIN must be 17 characters and start with ZFF.
          </small>
        )}
      </div>
      <div className="eeprom-flow">
        <div className="eeprom-actions">
          <label className="file-upload">
            <span className="file-upload-text">Select EEPROM file</span>
            <input
              id="f430-airbag-file"
              type="file"
              accept=".bin,.eep,.hex,.dat"
              onChange={handleFileChange}
            />
          </label>
          <span className={`flow-arrow ${stepSelected ? 'active' : ''}`}>→</span>
          <button type="button" onClick={handleUpload} disabled={isUploading || !vinIsValid}>
            {isUploading ? 'Uploading...' : 'Upload'}
          </button>
          <span className={`flow-arrow ${stepUploaded ? 'active' : ''}`}>→</span>
          <button
            type="button"
            onClick={handlePay}
            disabled={!upload || upload.alreadyPaid || isPaying || !Number.isFinite(upload.priceCents)}
          >
            {isPaying
              ? 'Redirecting...'
              : upload && Number.isFinite(upload.priceCents)
              ? `Pay $${(upload.priceCents / 100).toFixed(2)}`
              : 'Pay'}
          </button>
          <span className={`flow-arrow ${stepPaid ? 'active' : ''}`}>→</span>
          <button
            type="button"
            onClick={handleProcess}
            disabled={!upload || !upload.alreadyPaid || isProcessed}
          >
            {isProcessed ? 'Processed' : 'Process'}
          </button>
          <span className={`flow-arrow ${stepProcessed ? 'active' : ''}`}>→</span>
          <div className="download-wrapper">
            <button type="button" onClick={handleDownload} disabled={!canDownload}>
              Download
            </button>
            {upload?.alreadyPaid && <span className="paid-badge">Paid</span>}
          </div>
        </div>
        <div className="eeprom-progress">
          <span className={`progress-icon ${stepSelected ? 'active' : ''}`}>📄</span>
          <span className="progress-spacer" />
          <span className={`progress-icon ${stepUploaded ? 'active' : ''}`}>⬆️</span>
          <span className="progress-spacer" />
          <span className={`progress-icon ${stepPaid ? 'active' : ''}`}>💳</span>
          <span className="progress-spacer" />
          <span className={`progress-icon ${stepProcessed ? 'active' : ''}`}>⚙️</span>
          <span className="progress-spacer" />
          <span className={`progress-icon ${stepDownloaded ? 'active' : ''}`}>⬇️</span>
        </div>
      </div>
      {error && <div className="error-banner">{error}</div>}
      {successMessage && <div className="success-banner">{successMessage}</div>}


      <section className="eeprom-settings">
        <h3>Service Description</h3>
        <ul className="settings-list">
          <li>Erase crash data and clear airbag fault memory</li>
          <li>Reset ECU so it can be reused after deployment</li>
          <li>Maintain original configuration and coding</li>
        </ul>
      </section>

      <section className="eeprom-viewer">
        <h3>EEPROM Preview</h3>
        {!bytes && <p>Upload a binary EEPROM file to preview it here.</p>}
        {bytes && (
          <>
            <div className="eeprom-file-name">{fileName}</div>
            <div className="hex-view">
              <div className="hex-view-header">
                <span>Offset</span>
                <span>Hex</span>
                <span>ASCII</span>
              </div>
              <div className="hex-view-body">
                {rows.map((row) => (
                  <div key={row.offset} className="hex-row">
                    <span className="hex-offset">{row.offset}</span>
                    <span className="hex-bytes">
                      {row.hexBytes.map((value, index) => (
                        <span key={`${row.offset}-${index}`} className="hex-byte">
                          {value}
                        </span>
                      ))}
                    </span>
                    <span className="hex-ascii">
                      {row.ascii.split('').map((value, index) => (
                        <span
                          key={`${row.offset}-ascii-${index}`}
                          className="hex-ascii-char"
                        >
                          {value}
                        </span>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default F430Airbag;
